import { useState } from 'react'
import { Link } from 'react-router-dom'
import { errorMessage } from '../pb'
import type { MembersData } from '../feed/useMembersData'
import { authorName, postRequest, toggleAmen } from '../sisters'
import { useToast } from '../shell/Toast'
import type { CovellaUser } from '../session'

/**
 * The prayer board as a card beside the forum: a box to ask, the latest few requests, and a link
 * to the whole board. Open to every signed-in sister, Keeper or not, since the things the text
 * asks of her are never behind the paywall. The full board is `/sisters/prayers`.
 */
export function PrayerSide({ user, data }: { user: CovellaUser; data: MembersData }) {
  const toast = useToast()
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function post(event: React.FormEvent) {
    event.preventDefault()
    const body = draft.trim()
    if (!body) return
    setBusy(true)
    setError(null)
    try {
      await postRequest(user.id, body)
      setDraft('')
      await data.refreshBoard()
      toast('Posted to the sisters')
    } catch (err) {
      setError(errorMessage(err, 'Could not post that'))
    } finally {
      setBusy(false)
    }
  }

  async function amen(id: string, myAmenId: string | null) {
    try {
      await toggleAmen(id, user.id, myAmenId)
      await data.refreshBoard()
    } catch (err) {
      setError(errorMessage(err, 'Could not save that'))
    }
  }

  const latest = (data.board ?? []).slice(0, 4)

  return (
    <section className="card">
      <h3 className="card-title">
        <i className="ph-bold ph-hands-praying" aria-hidden="true" /> Prayer requests
      </h3>
      <form className="prayer-side-form" onSubmit={post}>
        <label className="visually-hidden" htmlFor="prayer-side">
          Ask for prayer
        </label>
        <textarea
          id="prayer-side"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask the sisters to pray with you"
        />
        <button className="btn-primary btn-sm" type="submit" disabled={busy || !draft.trim()}>
          {busy ? 'Posting' : 'Post'}
        </button>
      </form>
      {error && <p className="notice">{error}</p>}

      {data.board === null ? (
        <p className="cal-small">One moment</p>
      ) : latest.length === 0 ? (
        <p className="cal-small">No requests yet.</p>
      ) : (
        latest.map((entry) => (
          <div className="prayer-side-item" key={entry.id}>
            <p>{entry.body}</p>
            <p className="cal-small">
              {authorName(entry)} ·{' '}
              {entry.is_answered ? (
                <span className="chip chip-gold">Answered</span>
              ) : (
                <button
                  className={entry.myAmenId ? 'prayer-amen is-on' : 'prayer-amen'}
                  type="button"
                  onClick={() => void amen(entry.id, entry.myAmenId)}
                >
                  {entry.amenCount} {entry.amenCount === 1 ? 'amen' : 'amens'}
                </button>
              )}
            </p>
          </div>
        ))
      )}
      <Link className="cal-link" to="/sisters/prayers">
        See all prayers
      </Link>
    </section>
  )
}
